import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getFirestore, doc, getDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import TrackSearchSelector from '../components/search/TrackSearchSelector'; // Adjust path if needed

function CopyrightPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Parse URL parameters (e.g. /copyright?trackId=XYZ)
  const searchParams = new URLSearchParams(location.search);
  const queryTrackId = searchParams.get('trackId');

  const db = getFirestore();

  // Form State
  const [formData, setFormData] = useState({
    fullName: '',
    company: '',
    email: '',
    address: '',
    proofDescription: '',
    authorityDeclaration: false,
    digitalSignature: '',
  });

  // Selected Track Object State
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [manualFallbackId, setManualFallbackId] = useState('');

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Pre-populate Track if coming via direct URL query param (?trackId=XYZ)
  useEffect(() => {
    const fetchDirectTrack = async () => {
      if (queryTrackId) {
        try {
          const songDoc = await getDoc(doc(db, 'songs', queryTrackId));
          if (songDoc.exists()) {
            const data = songDoc.data();
            setSelectedTrack({
              id: songDoc.id,
              title: data.title,
              subtitle: data.artistName,
              imageUrl: data.albumArtworkURL,
            });
          } else {
            setManualFallbackId(queryTrackId);
          }
        } catch (err) {
          console.error('Error fetching pre-filled track:', err);
          setManualFallbackId(queryTrackId);
        }
      }
    };

    fetchDirectTrack();
  }, [queryTrackId, db]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const targetTrackId = selectedTrack ? selectedTrack.id : manualFallbackId;

    if (!targetTrackId) {
      toast.error('Please search and select a track to report.');
      return;
    }

    if (!formData.authorityDeclaration) {
      toast.error('You must certify the authority declaration under penalty of perjury.');
      return;
    }

    setLoading(true);

    try {
      // 1. Store report document in Firestore
      const reportRef = await addDoc(collection(db, 'copyright_reports'), {
        ...formData,
        trackId: targetTrackId,
        trackTitle: selectedTrack?.title || 'N/A',
        artistName: selectedTrack?.subtitle || 'N/A',
        status: 'pending',
        createdAt: serverTimestamp(),
      });

      // 2. Trigger email notification to support@atomicmusic.com.au
      await addDoc(collection(db, 'mail'), {
        to: ['support@atomicmusic.com.au'],
        message: {
          subject: `[Copyright Claim] Takedown Request - Track: ${selectedTrack?.title || targetTrackId}`,
          html: `
            <h2>New Infringement Report Submitted</h2>
            <p><strong>Report ID:</strong> ${reportRef.id}</p>
            <p><strong>Target Track ID:</strong> ${targetTrackId}</p>
            <p><strong>Track Title:</strong> ${selectedTrack?.title || 'Manual Entry'}</p>
            <p><strong>Artist:</strong> ${selectedTrack?.subtitle || 'N/A'}</p>
            <hr />
            <p><strong>Rights Holder:</strong> ${formData.fullName} (${formData.company || 'N/A'})</p>
            <p><strong>Contact Email:</strong> ${formData.email}</p>
            <p><strong>Address:</strong> ${formData.address}</p>
            <p><strong>Proof / Description:</strong> ${formData.proofDescription}</p>
            <p><strong>Digital Signature:</strong> ${formData.digitalSignature}</p>
          `,
        },
      });

      toast.success('Copyright report submitted successfully.');
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting copyright claim:', error);
      toast.error('Failed to submit report. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h1 style={styles.heading}>Report Submitted</h1>
          <p style={styles.text}>
            Thank you. Your takedown notice for <strong>{selectedTrack?.title || 'the selected track'}</strong> has been logged and sent to <strong>support@atomicmusic.com.au</strong>.
          </p>
          <p style={styles.text}>We target processing all validated reports within 24 hours.</p>
          <button style={styles.button} onClick={() => navigate('/')}>Return to Home</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.heading}>Copyright Infringement & Takedown Intake</h1>
        <p style={styles.subheading}>
          If you believe material on Atomic infringes upon your copyrighted work, please complete this form.
        </p>

        <form onSubmit={handleSubmit} style={styles.form}>
          <h2 style={styles.sectionHeader}>1. Rights Holder Information</h2>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Full Name *</label>
            <input
              type="text"
              name="fullName"
              required
              value={formData.fullName}
              onChange={handleChange}
              style={styles.input}
              placeholder="e.g. Jane Doe"
            />
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label}>Company / Organization (Optional)</label>
            <input
              type="text"
              name="company"
              value={formData.company}
              onChange={handleChange}
              style={styles.input}
              placeholder="e.g. Atomic Records LLC"
            />
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label}>Contact Email *</label>
            <input
              type="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              style={styles.input}
              placeholder="e.g. legal@example.com"
            />
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label}>Physical Address *</label>
            <textarea
              name="address"
              required
              rows={2}
              value={formData.address}
              onChange={handleChange}
              style={styles.input}
              placeholder="Full mailing address"
            />
          </div>

          <h2 style={styles.sectionHeader}>2. Infringing Material & Search</h2>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Search & Select Infringing Track *</label>
            <TrackSearchSelector
              selectedTrack={selectedTrack}
              onSelectTrack={setSelectedTrack}
              isDarkMode={true}
            />
            {!selectedTrack && manualFallbackId && (
              <p className="text-xs text-amber-400 mt-1">
                Pre-filled Track ID: <span className="font-mono">{manualFallbackId}</span>
              </p>
            )}
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label}>Proof / Description of Original Work *</label>
            <textarea
              name="proofDescription"
              required
              rows={4}
              value={formData.proofDescription}
              onChange={handleChange}
              style={styles.input}
              placeholder="Provide link to original release, ISWC code, copyright registration #, or details proving ownership."
            />
          </div>

          <h2 style={styles.sectionHeader}>3. Legal Declaration</h2>
          <div style={styles.checkboxGroup}>
            <input
              type="checkbox"
              id="authorityDeclaration"
              name="authorityDeclaration"
              checked={formData.authorityDeclaration}
              onChange={handleChange}
              style={styles.checkbox}
            />
            <label htmlFor="authorityDeclaration" style={styles.checkboxLabel}>
              I certify, under penalty of perjury and statutory declaration laws, that I am the copyright owner or an authorized agent acting on behalf of the owner of an exclusive right that is allegedly infringed. *
            </label>
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label}>Digital Signature (Type Full Legal Name) *</label>
            <input
              type="text"
              name="digitalSignature"
              required
              value={formData.digitalSignature}
              onChange={handleChange}
              style={styles.input}
              placeholder="e.g. Jane Mary Doe"
            />
          </div>

          <button type="submit" disabled={loading} style={styles.submitButton}>
            {loading ? 'Submitting Notice...' : 'Submit Takedown Notice'}
          </button>
        </form>
      </div>
    </div>
  );
}

const styles = {
  container: { maxWidth: '800px', margin: '40px auto', padding: '0 20px', fontFamily: 'sans-serif' },
  card: { backgroundColor: '#18181b', color: '#f4f4f5', padding: '32px', borderRadius: '8px', border: '1px solid #27272a' },
  heading: { fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' },
  subheading: { color: '#a1a1aa', marginBottom: '24px', fontSize: '14px' },
  sectionHeader: { fontSize: '18px', fontWeight: '600', marginTop: '24px', marginBottom: '12px', borderBottom: '1px solid #27272a', paddingBottom: '8px', color: '#e4e4e7' },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '14px', fontWeight: '500', color: '#d4d4d8' },
  input: { padding: '10px 12px', backgroundColor: '#09090b', border: '1px solid #3f3f46', borderRadius: '6px', color: '#fff', fontSize: '14px' },
  checkboxGroup: { display: 'flex', alignItems: 'flex-start', gap: '10px', margin: '12px 0' },
  checkbox: { marginTop: '4px' },
  checkboxLabel: { fontSize: '13px', color: '#a1a1aa', lineHeight: '1.4' },
  submitButton: { marginTop: '16px', padding: '12px', backgroundColor: '#dc2626', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' },
  button: { marginTop: '16px', padding: '10px 16px', backgroundColor: '#27272a', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' },
  text: { color: '#d4d4d8', marginBottom: '12px', lineHeight: '1.5' },
};

export { CopyrightPage };
export default CopyrightPage;